import { apiClient } from './api';
import type {
  HistoricalImportBatchDetail,
  HistoricalImportBatchSummary,
  HistoricalImportApplyResult,
  HistoricalImportMemberCandidate,
  HistoricalImportReserveUploadPayload,
  HistoricalImportUploadReservation,
  HistoricalImportPreparation,
} from '../types';

function importPath(squadId: number | string): string {
  return `/api/v1/web/squads/${squadId}/historical-imports`;
}

export async function getHistoricalImportPreparation(squadId: number | string): Promise<HistoricalImportPreparation> {
  return (await apiClient.get<HistoricalImportPreparation>(`${importPath(squadId)}/preparation`)).data;
}

export async function getHistoricalImportHandleCandidate(squadId: number | string, handle: string): Promise<HistoricalImportMemberCandidate> {
  return (await apiClient.get<HistoricalImportMemberCandidate>(`${importPath(squadId)}/handle-candidate`, {params: {handle}})).data;
}

export async function mapHistoricalImportGuest(squadId: number | string, batchId: string, aliasId: number, displayName: string, adultConfirmed: boolean): Promise<HistoricalImportBatchDetail> {
  return (await apiClient.put<HistoricalImportBatchDetail>(`${importPath(squadId)}/${batchId}/aliases/${aliasId}/guest`, {displayName, adultConfirmed})).data;
}
export async function skipHistoricalImportExisting(squadId: number | string,batchId: string,rowId: number,reference: string,sameMatchConfirmed: boolean): Promise<HistoricalImportBatchDetail> {
  return (await apiClient.post<HistoricalImportBatchDetail>(`${importPath(squadId)}/${batchId}/rows/${rowId}/skip-existing`, {reference,sameMatchConfirmed})).data;
}

export async function reserveHistoricalImportUpload(
  squadId: number | string,
  payload: HistoricalImportReserveUploadPayload
): Promise<HistoricalImportUploadReservation> {
  const response = await apiClient.post<HistoricalImportUploadReservation>(
    `${importPath(squadId)}/uploads`,
    payload
  );
  return response.data;
}

export async function uploadHistoricalImportCsv(
  reservation: HistoricalImportUploadReservation,
  file: File
): Promise<void> {
  const response = await fetch(reservation.uploadUrl, {
    method: 'PUT',
    headers: reservation.requiredHeaders,
    body: file,
  });
  if (!response.ok) {
    throw new Error('The score file could not be uploaded. Try again.');
  }
}

export async function completeHistoricalImportUpload(
  squadId: number | string,
  batchId: string
): Promise<HistoricalImportBatchDetail> {
  const response = await apiClient.post<HistoricalImportBatchDetail>(
    `${importPath(squadId)}/${batchId}/complete-upload`
  );
  return response.data;
}

export async function getHistoricalImportBatches(
  squadId: number | string,
  limit = 20
): Promise<HistoricalImportBatchSummary[]> {
  const response = await apiClient.get<HistoricalImportBatchSummary[]>(importPath(squadId), {
    params: { limit },
  });
  return response.data;
}

export async function getHistoricalImportBatch(
  squadId: number | string,
  batchId: string
): Promise<HistoricalImportBatchDetail> {
  const response = await apiClient.get<HistoricalImportBatchDetail>(
    `${importPath(squadId)}/${batchId}`
  );
  return response.data;
}

export async function getHistoricalImportMemberCandidates(
  squadId: number | string
): Promise<HistoricalImportMemberCandidate[]> {
  const response = await apiClient.get<HistoricalImportMemberCandidate[]>(
    `${importPath(squadId)}/member-candidates`
  );
  return response.data;
}

export async function mapHistoricalImportAlias(
  squadId: number | string,
  batchId: string,
  aliasId: number,
  userId: number
): Promise<HistoricalImportBatchDetail> {
  const response = await apiClient.put<HistoricalImportBatchDetail>(
    `${importPath(squadId)}/${batchId}/aliases/${aliasId}`,
    { userId }
  );
  return response.data;
}

export async function unmapHistoricalImportAlias(
  squadId: number | string,
  batchId: string,
  aliasId: number
): Promise<HistoricalImportBatchDetail> {
  const response = await apiClient.delete<HistoricalImportBatchDetail>(
    `${importPath(squadId)}/${batchId}/aliases/${aliasId}`
  );
  return response.data;
}

export async function validateHistoricalImport(
  squadId: number | string,
  batchId: string
): Promise<HistoricalImportBatchDetail> {
  const response = await apiClient.post<HistoricalImportBatchDetail>(
    `${importPath(squadId)}/${batchId}/validate`
  );
  return response.data;
}

export async function cancelHistoricalImport(
  squadId: number | string,
  batchId: string
): Promise<HistoricalImportBatchDetail> {
  const response = await apiClient.post<HistoricalImportBatchDetail>(
    `${importPath(squadId)}/${batchId}/cancel`
  );
  return response.data;
}

export async function applyHistoricalImport(
  squadId: number | string,
  batchId: string,
  idempotencyKey: string,
  reason: string
): Promise<HistoricalImportApplyResult> {
  const response = await apiClient.post<HistoricalImportApplyResult>(
    `${importPath(squadId)}/${batchId}/apply`,
    { idempotencyKey, reason }
  );
  return response.data;
}
